import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import PropTypes from 'prop-types';
import callApi from '../../utils/call-api.js';
import analyzeApiResponse from '../../utils/analyze-api-response.js';
import pearlUrl from '../../config/pearl-url.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import ErrorMessage from '../common/ErrorMessage.jsx';

const FILTER_ALL = '';

function formatWeekRange(row) {
    if (!row?.weekStart || !row?.weekEnd) return '—';
    const a = new Date(row.weekStart).toLocaleDateString();
    const b = new Date(row.weekEnd).toLocaleDateString();
    return `${a} – ${b}`;
}

function weekKeyFromRow(row) {
    if (!row?.weekStart) return '';
    try {
        return new Date(row.weekStart).toISOString();
    } catch {
        return '';
    }
}

/** Same as backend `getThisWeekDateRange`: Mon 00:00 UTC through next Mon 00:00 UTC (end exclusive). */
function getThisWeekDateRangeUtcMonday() {
    const startDate = new Date();
    startDate.setUTCHours(0, 0, 0, 0);
    const offsetToMonday = (startDate.getUTCDay() + 6) % 7;
    startDate.setUTCDate(startDate.getUTCDate() - offsetToMonday);
    const endDate = new Date(startDate);
    endDate.setUTCDate(endDate.getUTCDate() + 7);
    return { startDate, endDate };
}

const WEEK_START_MATCH_MS = 120000;

/**
 * One stable `<select>` value per audit week: min ISO key among rows whose weekStart
 * is within the same window the backend uses for sign-off matching.
 */
function canonicalWeekFilterKeyForRow(row, rowList) {
    if (!row?.weekStart || !rowList?.length) return weekKeyFromRow(row);
    const anchor = new Date(row.weekStart).getTime();
    if (!Number.isFinite(anchor)) return weekKeyFromRow(row);
    const keys = rowList
        .map((r) => {
            if (!r?.weekStart) return '';
            const ws = new Date(r.weekStart).getTime();
            if (!Number.isFinite(ws)) return '';
            if (Math.abs(ws - anchor) > WEEK_START_MATCH_MS) return '';
            return weekKeyFromRow(r);
        })
        .filter(Boolean);
    if (!keys.length) return weekKeyFromRow(row);
    keys.sort();
    return keys[0];
}

/**
 * Default week filter: half-open [weekStart, weekEnd) like stored audit periods
 * (weekEnd = next Monday 00:00 UTC), else any row whose weekStart matches this
 * week's Monday within the same window the API uses (~2 min, JSON rounding).
 */
function defaultWeekFilterKey(rowList) {
    if (!rowList?.length) return FILTER_ALL;
    const now = Date.now();

    const findHalfOpen = () =>
        rowList.find((r) => {
            if (!r?.weekStart || !r?.weekEnd) return false;
            const ws = new Date(r.weekStart).getTime();
            const we = new Date(r.weekEnd).getTime();
            if (!Number.isFinite(ws) || !Number.isFinite(we)) return false;
            return now >= ws && now < we;
        });

    const findNearCanonicalMonday = () => {
        const { startDate } = getThisWeekDateRangeUtcMonday();
        const target = startDate.getTime();
        return rowList.find((r) => {
            if (!r?.weekStart) return false;
            const ws = new Date(r.weekStart).getTime();
            return (
                Number.isFinite(ws) &&
                Math.abs(ws - target) <= WEEK_START_MATCH_MS
            );
        });
    };

    const match = findHalfOpen() || findNearCanonicalMonday();
    if (!match) return FILTER_ALL;
    return canonicalWeekFilterKeyForRow(match, rowList);
}

function ticketCountForRow(row) {
    if (!Array.isArray(row?.auditTicketHumanIds)) return 0;
    return row.auditTicketHumanIds.filter(Boolean).length;
}

function reviewFlagIdsFromRow(row) {
    if (!Array.isArray(row?.reviewFlagTicketHumanIds)) return [];
    return [...new Set(row.reviewFlagTicketHumanIds)]
        .map((id) => (id != null ? String(id).trim() : ''))
        .filter(Boolean);
}

/**
 * Weekly audit tickets are closed in Pearl; use /chat/closed/ like Pearl ticket UI.
 * Falls back to search only when we have no Mongo ticket id.
 */
function pearlTicketHref(pearlMongoId) {
    const id = pearlMongoId != null ? String(pearlMongoId).trim() : '';
    if (id) return `${pearlUrl}/chat/closed/${id}`;
    return `${pearlUrl}/search`;
}

function teamValue(row) {
    return (row.teamHdName || '').trim();
}

function startOfLocalDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x.getTime();
}

function endOfLocalDay(d) {
    const x = new Date(d);
    x.setHours(23, 59, 59, 999);
    return x.getTime();
}

function escapeCsvCell(s) {
    const t = String(s ?? '');
    if (/[",\r\n]/.test(t)) {
        return `"${t.replace(/"/g, '""')}"`;
    }
    return t;
}

function buildFilteredRows(rows, filters, sortKey) {
    const q = (filters.search || '').trim().toLowerCase();
    const teamEq = filters.team;
    const weekEq = filters.week;
    const techEq = filters.tech;

    const vFrom = filters.validatedFrom
        ? startOfLocalDay(filters.validatedFrom)
        : null;
    const vTo = filters.validatedTo ? endOfLocalDay(filters.validatedTo) : null;

    const tMin =
        filters.ticketsMin !== '' && filters.ticketsMin != null
            ? Number(filters.ticketsMin)
            : null;
    const tMax =
        filters.ticketsMax !== '' && filters.ticketsMax != null
            ? Number(filters.ticketsMax)
            : null;

    const fMin =
        filters.failMin !== '' && filters.failMin != null
            ? Number(filters.failMin)
            : null;
    const fMax =
        filters.failMax !== '' && filters.failMax != null
            ? Number(filters.failMax)
            : null;

    let list = rows.filter((row) => {
        if (weekEq && weekKeyFromRow(row) !== weekEq) return false;

        if (teamEq) {
            const tv = teamValue(row);
            if (teamEq === '__empty__') {
                if (tv !== '') return false;
            } else if (tv !== teamEq) return false;
        }

        if (techEq && String(row.techOracleUserId) !== techEq) {
            return false;
        }

        if (q) {
            const tech = (row.techDisplayName || '').toLowerCase();
            const team = teamValue(row).toLowerCase();
            const initials = (row.initials || '').toLowerCase();
            const fb = (row.feedbackNotes || '').toLowerCase();
            const ids = Array.isArray(row.auditTicketHumanIds)
                ? row.auditTicketHumanIds
                      .filter(Boolean)
                      .join(' ')
                      .toLowerCase()
                : '';
            const hay = `${tech} ${team} ${initials} ${fb} ${ids}`;
            if (!hay.includes(q)) return false;
        }

        const validatedAt = row.leadValidatedAt
            ? new Date(row.leadValidatedAt).getTime()
            : null;
        if (vFrom != null) {
            if (validatedAt == null || validatedAt < vFrom) return false;
        }
        if (vTo != null) {
            if (validatedAt == null || validatedAt > vTo) return false;
        }

        const tc = ticketCountForRow(row);
        if (tMin != null && !Number.isNaN(tMin) && tc < tMin) return false;
        if (tMax != null && !Number.isNaN(tMax) && tc > tMax) return false;

        const fp = row.publicScoreFailedPct;
        if (fMin != null && !Number.isNaN(fMin)) {
            if (fp == null || fp < fMin) return false;
        }
        if (fMax != null && !Number.isNaN(fMax)) {
            if (fp == null || fp > fMax) return false;
        }

        if (filters.hasFeedback) {
            if (!(row.feedbackNotes || '').trim()) return false;
        }

        if (filters.hasPublicScore) {
            if (
                row.publicScoreFailedPct == null &&
                row.publicScoreClosedTotal == null
            ) {
                return false;
            }
        }

        return true;
    });

    const byWeek = (a, b) => {
        const ta = a.weekStart ? new Date(a.weekStart).getTime() : 0;
        const tb = b.weekStart ? new Date(b.weekStart).getTime() : 0;
        return ta - tb;
    };

    list = [...list];
    switch (sortKey) {
        case 'weekAsc':
            list.sort((a, b) => byWeek(a, b));
            break;
        case 'weekDesc':
            list.sort((a, b) => -byWeek(a, b));
            break;
        case 'teamAsc':
            list.sort((a, b) =>
                teamValue(a).localeCompare(teamValue(b), undefined, {
                    sensitivity: 'base'
                })
            );
            break;
        case 'techAsc':
            list.sort((a, b) =>
                (a.techDisplayName || '').localeCompare(
                    b.techDisplayName || '',
                    undefined,
                    { sensitivity: 'base' }
                )
            );
            break;
        case 'validatedDesc': {
            list.sort((a, b) => {
                const ta = a.leadValidatedAt
                    ? new Date(a.leadValidatedAt).getTime()
                    : 0;
                const tb = b.leadValidatedAt
                    ? new Date(b.leadValidatedAt).getTime()
                    : 0;
                return tb - ta;
            });
            break;
        }
        case 'ticketsDesc':
            list.sort((a, b) => ticketCountForRow(b) - ticketCountForRow(a));
            break;
        case 'failDesc':
            list.sort((a, b) => {
                const fa = a.publicScoreFailedPct;
                const fb = b.publicScoreFailedPct;
                if (fa == null && fb == null) return 0;
                if (fa == null) return 1;
                if (fb == null) return -1;
                return fb - fa;
            });
            break;
        default:
            list.sort((a, b) => -byWeek(a, b));
    }

    return list;
}

function downloadFilteredCsv(rows) {
    const header = [
        'Week start',
        'Week end',
        'Technician',
        'Team',
        'Initials',
        'Validated',
        'Ticket count',
        'Fail %',
        'Closed total',
        'Ticket human IDs',
        'Pearl ticket IDs',
        'Feedback'
    ];
    const lines = [
        header.map(escapeCsvCell).join(','),
        ...rows.map((row) => {
            const ws = row.weekStart
                ? new Date(row.weekStart).toISOString()
                : '';
            const we = row.weekEnd ? new Date(row.weekEnd).toISOString() : '';
            const val = row.leadValidatedAt
                ? new Date(row.leadValidatedAt).toISOString()
                : '';
            const humanJoined = Array.isArray(row.auditTicketHumanIds)
                ? row.auditTicketHumanIds.filter(Boolean).join('; ')
                : '';
            const pearlJoined = Array.isArray(row.auditTicketPearlIds)
                ? row.auditTicketPearlIds
                      .map((x) => String(x || '').trim())
                      .filter(Boolean)
                      .join('; ')
                : '';
            const line = [
                ws,
                we,
                row.techDisplayName || '',
                teamValue(row),
                row.initials || '',
                val,
                ticketCountForRow(row),
                row.publicScoreFailedPct ?? '',
                row.publicScoreClosedTotal ?? '',
                humanJoined,
                pearlJoined,
                (row.feedbackNotes || '').replace(/\r?\n/g, ' ')
            ];
            return line.map(escapeCsvCell).join(',');
        })
    ];
    const csv = lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = globalThis.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `completed-audits-${new Date()
        .toISOString()
        .slice(0, 10)}.csv`;
    a.click();
    globalThis.URL.revokeObjectURL(url);
}

/** Severity bucket for a failed-percentage value, used to color the stat tile. */
function failPctTone(pct) {
    if (pct == null || Number.isNaN(Number(pct))) return 'neutral';
    const n = Number(pct);
    if (n >= 25) return 'high';
    if (n >= 10) return 'mid';
    return 'low';
}

/**
 * Grouped sign-off detail: header (who/when), key stats, secondary metadata,
 * audit tickets, and feedback. Replaces the old flat label/value list so the
 * numbers that matter (tickets, fail %, flags) read at a glance.
 *
 * @param {{ selected: object, onClose: () => void }} props
 * @returns {JSX.Element}
 */
const SignoffDetailPanel = ({ selected, onClose }) => {
    const ticketCount = ticketCountForRow(selected);
    const flagIds = reviewFlagIdsFromRow(selected);
    const flagCount = flagIds.length;
    const flagSet = new Set(flagIds);
    const failPct = selected.publicScoreFailedPct;
    const closedTotal = selected.publicScoreClosedTotal;

    const humanIds = Array.isArray(selected.auditTicketHumanIds)
        ? selected.auditTicketHumanIds
        : [];
    const hasTickets = humanIds.some(
        (id) => id != null && String(id).trim() !== ''
    );

    return (
        <div
            className="audit-completed-detail"
            role="region"
            aria-label="Sign-off detail">
            <div className="audit-completed-detail__header">
                <div className="audit-completed-detail__heading">
                    <span className="audit-completed-detail__eyebrow">
                        Sign-off detail
                    </span>
                    <h3>{selected.techDisplayName?.trim() || 'Technician'}</h3>
                    <p className="audit-completed-detail__subtitle">
                        {formatWeekRange(selected)}
                        {selected.teamHdName?.trim()
                            ? ` · ${selected.teamHdName.trim()}`
                            : ''}
                    </p>
                </div>
                <button
                    type="button"
                    className="audit-completed-detail__close"
                    onClick={onClose}
                    aria-label="Close detail">
                    Close
                </button>
            </div>

            <div className="audit-completed-detail__stats">
                <div className="audit-completed-detail__stat">
                    <span className="audit-completed-detail__stat-value">
                        {ticketCount}
                    </span>
                    <span className="audit-completed-detail__stat-label">
                        Tickets
                    </span>
                </div>
                <div
                    className={`audit-completed-detail__stat audit-completed-detail__stat--${failPctTone(
                        failPct
                    )}`}>
                    <span className="audit-completed-detail__stat-value">
                        {failPct != null ? `${failPct}%` : '—'}
                    </span>
                    <span className="audit-completed-detail__stat-label">
                        Fail rate
                    </span>
                </div>
                <div
                    className={`audit-completed-detail__stat${
                        flagCount > 0
                            ? ' audit-completed-detail__stat--flag'
                            : ''
                    }`}>
                    <span className="audit-completed-detail__stat-value">
                        {flagCount}
                    </span>
                    <span className="audit-completed-detail__stat-label">
                        Flagged
                    </span>
                </div>
            </div>

            <dl className="audit-completed-detail__grid">
                <dt>Initials</dt>
                <dd>{selected.initials || '—'}</dd>
                <dt>Validated</dt>
                <dd>
                    {selected.leadValidatedAt
                        ? new Date(selected.leadValidatedAt).toLocaleString()
                        : '—'}
                </dd>
                <dt>Recorded</dt>
                <dd>
                    {selected.updatedAt
                        ? new Date(selected.updatedAt).toLocaleString()
                        : '—'}
                </dd>
                <dt>Public score</dt>
                <dd>
                    {closedTotal != null ? (
                        <>
                            {closedTotal} tickets
                            {failPct != null ? ` · ${failPct}% failed` : ''}
                            <span className="audit-completed-detail__hint">
                                {' '}
                                (snapshot)
                            </span>
                        </>
                    ) : (
                        '—'
                    )}
                </dd>
            </dl>

            <section className="audit-completed-detail__section">
                <div className="audit-completed-detail__section-head">
                    <h4>Audit tickets</h4>
                    {flagCount > 0 ? (
                        <span className="audit-completed-detail__section-note">
                            {flagCount} flagged for follow-up
                        </span>
                    ) : null}
                </div>
                {hasTickets ? (
                    <ul className="audit-completed-detail__ticket-links">
                        {humanIds.map((hidRaw, idx) => {
                            const hid =
                                hidRaw != null ? String(hidRaw).trim() : '';
                            if (!hid) return null;
                            const pearlRaw = Array.isArray(
                                selected.auditTicketPearlIds
                            )
                                ? selected.auditTicketPearlIds[idx]
                                : '';
                            const href = pearlTicketHref(pearlRaw);
                            const isDirect =
                                pearlRaw != null &&
                                String(pearlRaw).trim() !== '';
                            const isFlagged = flagSet.has(hid);
                            return (
                                <li
                                    key={`${idx}-${hid}`}
                                    className={
                                        isFlagged
                                            ? 'audit-completed-detail__ticket--flagged'
                                            : undefined
                                    }>
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="audit-ticket-row__id-link"
                                        title={
                                            isDirect
                                                ? 'Open in Pearl (new tab)'
                                                : 'Pearl search (older sign-offs may omit ticket id; re-save sign-off for a direct link)'
                                        }>
                                        {hid}
                                    </a>
                                    {isFlagged ? (
                                        <span className="audit-completed-detail__flag-pill">
                                            Flagged
                                        </span>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="audit-completed-detail__empty">
                        No audit tickets recorded.
                    </p>
                )}
            </section>

            <section className="audit-completed-detail__section">
                <div className="audit-completed-detail__section-head">
                    <h4>Feedback to tech</h4>
                </div>
                {selected.feedbackNotes?.trim() ? (
                    <p className="audit-completed-detail__feedback">
                        {selected.feedbackNotes}
                    </p>
                ) : (
                    <p className="audit-completed-detail__empty">
                        No feedback recorded.
                    </p>
                )}
            </section>
        </div>
    );
};

SignoffDetailPanel.propTypes = {
    selected: PropTypes.shape({
        techDisplayName: PropTypes.string,
        teamHdName: PropTypes.string,
        initials: PropTypes.string,
        leadValidatedAt: PropTypes.string,
        updatedAt: PropTypes.string,
        weekStart: PropTypes.string,
        weekEnd: PropTypes.string,
        publicScoreFailedPct: PropTypes.number,
        publicScoreClosedTotal: PropTypes.number,
        feedbackNotes: PropTypes.string,
        auditTicketHumanIds: PropTypes.array,
        auditTicketPearlIds: PropTypes.array,
        reviewFlagTicketHumanIds: PropTypes.array
    }).isRequired,
    onClose: PropTypes.func.isRequired
};

const DEFAULT_FILTERS = {
    team: FILTER_ALL,
    week: FILTER_ALL,
    tech: FILTER_ALL,
    search: '',
    validatedFrom: '',
    validatedTo: '',
    ticketsMin: '',
    ticketsMax: '',
    failMin: '',
    failMax: '',
    hasFeedback: false,
    hasPublicScore: false
};

/**
 * Management reporting: completed weekly audit sign-offs for the signed-in lead
 * (high-level view of which audits are complete). Row opens detail (tickets, feedback,
 * score snapshot). Pending audits and monthly per-technician rollup live on Audit tech.
 *
 * @returns {JSX.Element}
 */
const CompletedAuditsView = () => {
    const [rows, setRows] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selected, setSelected] = useState(null);
    const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
    const [sortKey, setSortKey] = useState('weekDesc');
    const weekAutoDefaultDoneRef = useRef(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await callApi(
                '/reporting/weekly-audit/signoffs',
                'get'
            );
            if (!res.ok) {
                const msg = await analyzeApiResponse(res);
                throw new Error(msg || 'Failed to load sign-offs');
            }
            const data = await res.json();
            setRows(Array.isArray(data) ? data : []);
        } catch (e) {
            setError(e);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useLayoutEffect(() => {
        if (!rows?.length) {
            weekAutoDefaultDoneRef.current = false;
            return;
        }
        if (weekAutoDefaultDoneRef.current) return;
        weekAutoDefaultDoneRef.current = true;
        const wk = defaultWeekFilterKey(rows);
        if (wk) {
            setFilters((f) => ({ ...f, week: wk }));
        }
    }, [rows]);

    const weekOptions = useMemo(() => {
        if (!rows?.length) return [];
        const map = new Map();
        rows.forEach((r) => {
            const k = weekKeyFromRow(r);
            if (k && !map.has(k)) map.set(k, formatWeekRange(r));
        });
        return [...map.entries()]
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([value, label]) => ({ value, label }));
    }, [rows]);

    /** Aggregates loaded sign-offs by audit week (management: complete volume by week). */
    const weekSummaryRows = useMemo(() => {
        if (!rows?.length) return [];
        const byKey = new Map();
        rows.forEach((r) => {
            const k = weekKeyFromRow(r);
            if (!k) return;
            if (!byKey.has(k)) {
                byKey.set(k, {
                    weekStart: r.weekStart,
                    weekEnd: r.weekEnd,
                    techIds: new Set(),
                    signoffRows: 0
                });
            }
            const g = byKey.get(k);
            g.signoffRows += 1;
            if (r.techOracleUserId != null) {
                g.techIds.add(String(r.techOracleUserId));
            }
        });
        return [...byKey.entries()]
            .map(([weekKey, v]) => ({
                weekKey,
                weekLabel: formatWeekRange({
                    weekStart: v.weekStart,
                    weekEnd: v.weekEnd
                }),
                signoffRows: v.signoffRows,
                distinctTechs: v.techIds.size
            }))
            .sort((a, b) => b.weekKey.localeCompare(a.weekKey));
    }, [rows]);

    const teamOptions = useMemo(() => {
        if (!rows?.length) return [];
        const set = new Set();
        let hasEmpty = false;
        rows.forEach((r) => {
            const t = teamValue(r);
            if (!t) hasEmpty = true;
            else set.add(t);
        });
        const names = [...set].sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: 'base' })
        );
        const out = names.map((name) => ({ value: name, label: name }));
        if (hasEmpty) {
            out.push({ value: '__empty__', label: '(No team)' });
        }
        return out;
    }, [rows]);

    const techOptions = useMemo(() => {
        if (!rows?.length) return [];
        const byId = new Map();
        rows.forEach((r) => {
            const id = String(r.techOracleUserId || '');
            if (id) {
                const name = (r.techDisplayName || '').trim() || 'Unknown';
                if (!byId.has(id)) byId.set(id, name);
            }
        });
        return [...byId.entries()]
            .sort((a, b) =>
                a[1].localeCompare(b[1], undefined, { sensitivity: 'base' })
            )
            .map(([value, label]) => ({ value, label }));
    }, [rows]);

    const filteredRows = useMemo(
        () => (rows?.length ? buildFilteredRows(rows, filters, sortKey) : []),
        [rows, filters, sortKey]
    );

    const baselineFilters = useMemo(
        () => ({
            ...DEFAULT_FILTERS,
            week: defaultWeekFilterKey(rows ?? [])
        }),
        [rows]
    );

    const filtersActive = useMemo(() => {
        const b = baselineFilters;
        return !(
            filters.team === b.team &&
            filters.week === b.week &&
            filters.tech === b.tech &&
            (filters.search || '').trim() === (b.search || '').trim() &&
            filters.validatedFrom === b.validatedFrom &&
            filters.validatedTo === b.validatedTo &&
            filters.ticketsMin === b.ticketsMin &&
            filters.ticketsMax === b.ticketsMax &&
            filters.failMin === b.failMin &&
            filters.failMax === b.failMax &&
            filters.hasFeedback === b.hasFeedback &&
            filters.hasPublicScore === b.hasPublicScore
        );
    }, [filters, baselineFilters]);

    useEffect(() => {
        if (!selected) return;
        const still = filteredRows.some((r) => r._id === selected._id);
        if (!still) setSelected(null);
    }, [filteredRows, selected]);

    const setFilter = (key, value) => {
        setFilters((f) => ({ ...f, [key]: value }));
    };

    const resetFilters = useCallback(() => {
        setFilters({
            ...DEFAULT_FILTERS,
            week: defaultWeekFilterKey(rows ?? [])
        });
        setSortKey('weekDesc');
    }, [rows]);

    const onRowKeyDown = (row, e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setSelected((s) => (s?._id === row._id ? null : row));
        }
    };

    const showTableSection = !loading && rows && rows.length > 0;

    return (
        <div className="audit-completed-page">
            <div className="audit-completed-page__intro">
                <p>
                    Completed weekly audit sign-offs (newest first); pending
                    audits and monthly technician summary are on Audit tech.
                </p>
                <button
                    type="button"
                    className="newsc-btn audit-completed-page__refresh"
                    onClick={load}
                    disabled={loading}>
                    Refresh
                </button>
            </div>

            {loading && <LoadingSpinner />}
            {error && <ErrorMessage message={error.message} />}

            {!loading && rows && rows.length === 0 && (
                <div className="audit-weekly-empty">
                    <h3>No completed audits yet</h3>
                    <p>
                        Run an audit from <strong>Audit tech</strong> and submit
                        a sign-off to build this list.
                    </p>
                </div>
            )}

            {showTableSection && (
                <>
                    {weekSummaryRows.length > 0 ? (
                        <div className="audit-completed-week-summary">
                            <h3 className="audit-completed-week-summary__title">
                                By audit week (this list)
                            </h3>
                            <p className="audit-completed-week-summary__desc">
                                Completed sign-off rows and distinct technicians
                                per week. Use <strong>Audit tech</strong> for
                                who is still pending in the current period.
                            </p>
                            <div className="audit-completed-week-summary__table-wrap">
                                <table className="audit-completed-week-summary__table">
                                    <thead>
                                        <tr>
                                            <th scope="col">Week</th>
                                            <th scope="col">Sign-offs</th>
                                            <th scope="col">Technicians</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {weekSummaryRows.map((w) => (
                                            <tr key={w.weekKey}>
                                                <td>{w.weekLabel}</td>
                                                <td>{w.signoffRows}</td>
                                                <td>{w.distinctTechs}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                    <div className="audit-completed-filters">
                        <div className="audit-completed-filters__primary">
                            <div className="audit-completed-filters__field">
                                <label htmlFor="acf-week">Week</label>
                                <select
                                    id="acf-week"
                                    className="audit-completed-filters__select"
                                    value={filters.week}
                                    onChange={(e) =>
                                        setFilter('week', e.target.value)
                                    }>
                                    <option value="">All weeks</option>
                                    {weekOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="audit-completed-filters__field">
                                <label htmlFor="acf-team">Team</label>
                                <select
                                    id="acf-team"
                                    className="audit-completed-filters__select"
                                    value={filters.team}
                                    onChange={(e) =>
                                        setFilter('team', e.target.value)
                                    }>
                                    <option value="">All teams</option>
                                    {teamOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="audit-completed-filters__field">
                                <label htmlFor="acf-tech">Technician</label>
                                <select
                                    id="acf-tech"
                                    className="audit-completed-filters__select"
                                    value={filters.tech}
                                    onChange={(e) =>
                                        setFilter('tech', e.target.value)
                                    }>
                                    <option value="">All technicians</option>
                                    {techOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="audit-completed-filters__field audit-completed-filters__field--grow">
                                <label htmlFor="acf-search">Search</label>
                                <input
                                    id="acf-search"
                                    type="search"
                                    className="audit-completed-filters__input"
                                    placeholder="Name, team, initials, ticket ID, feedback…"
                                    value={filters.search}
                                    onChange={(e) =>
                                        setFilter('search', e.target.value)
                                    }
                                    autoComplete="off"
                                />
                            </div>
                            <div className="audit-completed-filters__field">
                                <label htmlFor="acf-sort">Sort by</label>
                                <select
                                    id="acf-sort"
                                    className="audit-completed-filters__select"
                                    value={sortKey}
                                    onChange={(e) =>
                                        setSortKey(e.target.value)
                                    }>
                                    <option value="weekDesc">
                                        Week (newest first)
                                    </option>
                                    <option value="weekAsc">
                                        Week (oldest first)
                                    </option>
                                    <option value="techAsc">
                                        Technician (A–Z)
                                    </option>
                                    <option value="teamAsc">Team (A–Z)</option>
                                    <option value="validatedDesc">
                                        Validated (recent first)
                                    </option>
                                    <option value="ticketsDesc">
                                        Ticket count (high first)
                                    </option>
                                    <option value="failDesc">
                                        Fail % (high first)
                                    </option>
                                </select>
                            </div>
                        </div>

                        <details className="audit-completed-filters__advanced">
                            <summary>
                                More filters — dates, counts, score
                            </summary>
                            <div className="audit-completed-filters__advanced-grid">
                                <div className="audit-completed-filters__field">
                                    <label htmlFor="acf-vfrom">
                                        Validated from
                                    </label>
                                    <input
                                        id="acf-vfrom"
                                        type="date"
                                        className="audit-completed-filters__input"
                                        value={filters.validatedFrom}
                                        onChange={(e) =>
                                            setFilter(
                                                'validatedFrom',
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                                <div className="audit-completed-filters__field">
                                    <label htmlFor="acf-vto">
                                        Validated to
                                    </label>
                                    <input
                                        id="acf-vto"
                                        type="date"
                                        className="audit-completed-filters__input"
                                        value={filters.validatedTo}
                                        onChange={(e) =>
                                            setFilter(
                                                'validatedTo',
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                                <div className="audit-completed-filters__field">
                                    <label htmlFor="acf-tmin">
                                        Min tickets
                                    </label>
                                    <input
                                        id="acf-tmin"
                                        type="number"
                                        min={0}
                                        step={1}
                                        className="audit-completed-filters__input"
                                        placeholder="0"
                                        value={filters.ticketsMin}
                                        onChange={(e) =>
                                            setFilter(
                                                'ticketsMin',
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                                <div className="audit-completed-filters__field">
                                    <label htmlFor="acf-tmax">
                                        Max tickets
                                    </label>
                                    <input
                                        id="acf-tmax"
                                        type="number"
                                        min={0}
                                        step={1}
                                        className="audit-completed-filters__input"
                                        placeholder="∞"
                                        value={filters.ticketsMax}
                                        onChange={(e) =>
                                            setFilter(
                                                'ticketsMax',
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                                <div className="audit-completed-filters__field">
                                    <label htmlFor="acf-fmin">Min fail %</label>
                                    <input
                                        id="acf-fmin"
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={1}
                                        className="audit-completed-filters__input"
                                        placeholder="0"
                                        value={filters.failMin}
                                        onChange={(e) =>
                                            setFilter('failMin', e.target.value)
                                        }
                                    />
                                </div>
                                <div className="audit-completed-filters__field">
                                    <label htmlFor="acf-fmax">Max fail %</label>
                                    <input
                                        id="acf-fmax"
                                        type="number"
                                        min={0}
                                        max={100}
                                        step={1}
                                        className="audit-completed-filters__input"
                                        placeholder="100"
                                        value={filters.failMax}
                                        onChange={(e) =>
                                            setFilter('failMax', e.target.value)
                                        }
                                    />
                                </div>
                                <label className="audit-completed-filters__check">
                                    <input
                                        type="checkbox"
                                        checked={filters.hasFeedback}
                                        onChange={(e) =>
                                            setFilter(
                                                'hasFeedback',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    Only with feedback notes
                                </label>
                                <label className="audit-completed-filters__check">
                                    <input
                                        type="checkbox"
                                        checked={filters.hasPublicScore}
                                        onChange={(e) =>
                                            setFilter(
                                                'hasPublicScore',
                                                e.target.checked
                                            )
                                        }
                                    />
                                    Only with public score snapshot
                                </label>
                            </div>
                        </details>

                        <div className="audit-completed-filters__toolbar">
                            <p
                                className="audit-completed-filters__count"
                                role="status">
                                Showing <strong>{filteredRows.length}</strong>{' '}
                                of <strong>{rows.length}</strong>
                                {filtersActive ? ' (filtered)' : ''}
                            </p>
                            <div className="audit-completed-filters__actions">
                                <button
                                    type="button"
                                    className="audit-completed-filters__btn"
                                    onClick={resetFilters}
                                    disabled={
                                        !filtersActive && sortKey === 'weekDesc'
                                    }>
                                    Reset filters
                                </button>
                                <button
                                    type="button"
                                    className="audit-completed-filters__btn audit-completed-filters__btn--primary"
                                    onClick={() =>
                                        downloadFilteredCsv(filteredRows)
                                    }
                                    disabled={filteredRows.length === 0}>
                                    Export CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    {filteredRows.length === 0 ? (
                        <div className="audit-completed-empty-filter">
                            <h3>No audits match your filters</h3>
                            <p>
                                Try widening the week, team, or search, or{' '}
                                <button
                                    type="button"
                                    className="audit-completed-empty-filter__reset"
                                    onClick={resetFilters}>
                                    clear all filters
                                </button>
                                .
                            </p>
                        </div>
                    ) : (
                        <div className="audit-completed-layout">
                            <div className="audit-completed-table-wrap">
                                <table
                                    className="audit-completed-table"
                                    role="grid"
                                    aria-label="Completed weekly audit sign-offs">
                                    <thead>
                                        <tr>
                                            <th scope="col">Week</th>
                                            <th scope="col">Technician</th>
                                            <th scope="col">Team</th>
                                            <th scope="col">Initials</th>
                                            <th scope="col">Validated</th>
                                            <th scope="col">Tickets</th>
                                            <th scope="col">Fail %</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRows.map((row) => {
                                            const isSel =
                                                selected?._id === row._id;
                                            const ticketCount =
                                                ticketCountForRow(row);
                                            return (
                                                <tr
                                                    key={row._id}
                                                    className={
                                                        isSel
                                                            ? 'audit-completed-table__row audit-completed-table__row--selected'
                                                            : 'audit-completed-table__row'
                                                    }
                                                    role="button"
                                                    tabIndex={0}
                                                    aria-selected={isSel}
                                                    onClick={() =>
                                                        setSelected((s) =>
                                                            s?._id === row._id
                                                                ? null
                                                                : row
                                                        )
                                                    }
                                                    onKeyDown={(e) =>
                                                        onRowKeyDown(row, e)
                                                    }>
                                                    <td>
                                                        {formatWeekRange(row)}
                                                    </td>
                                                    <td>
                                                        {row.techDisplayName?.trim() ||
                                                            '—'}
                                                    </td>
                                                    <td>
                                                        {row.teamHdName?.trim() ||
                                                            '—'}
                                                    </td>
                                                    <td>
                                                        {row.initials || '—'}
                                                    </td>
                                                    <td>
                                                        {row.leadValidatedAt
                                                            ? new Date(
                                                                  row.leadValidatedAt
                                                              ).toLocaleDateString()
                                                            : '—'}
                                                    </td>
                                                    <td>{ticketCount}</td>
                                                    <td>
                                                        {row.publicScoreFailedPct !=
                                                        null
                                                            ? `${row.publicScoreFailedPct}%`
                                                            : '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {selected ? (
                                <SignoffDetailPanel
                                    selected={selected}
                                    onClose={() => setSelected(null)}
                                />
                            ) : null}
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default CompletedAuditsView;
